<template>
    <div class="cg-page">
        <!-- Header -->
        <div class="cg-header">
            <div class="cg-header-left">
                <div class="cg-icon-wrap"><i class="fas fa-comments"></i></div>
                <h2 class="cg-title">Communication Groups</h2>
                <span class="cg-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.EDIT_COMMUNICATION_GROUPS">
                <button class="cg-add-btn" @click="showForm = true">
                    <i class="fas fa-plus"></i>
                    New Group
                </button>
            </AllowPermission>
        </div>

        <!-- Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Group' : 'New Group'" @close="closeForm">
            <Form :fields="createFields" title="Communication Group" submitUrl="/communication-groups"
                updateRootUrl="/communication-groups" @success="handleFormSuccess" @cancel="closeForm"
                :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="cg-loading"><div class="cg-spinner"></div><span>Loading groups...</span></div>

        <!-- Empty State -->
        <div v-else-if="!items.length" class="cg-empty">
            <div class="cg-empty-icon"><i class="fas fa-comments"></i></div>
            <h3>No communication groups found</h3>
            <p>Create a group to start messaging</p>
        </div>

        <!-- Table -->
        <div v-else class="cg-table-wrap">
            <table class="cg-table">
                <thead>
                    <tr>
                        <th>Group Name</th>
                        <th>Description</th>
                        <th class="text-center">Last Message</th>
                        <th class="text-center">Permissions</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="group in items" :key="group.id">
                        <td class="cg-name-cell">
                            <div class="cg-group-avatar" v-if="group.thumbnailUrl">
                                <img :src="group.thumbnailUrl" :alt="group.name" />
                            </div>
                            <div v-else class="cg-group-avatar cg-avatar-default">
                                <i class="fas fa-users"></i>
                            </div>
                            <span class="cg-name">{{ group.name }}</span>
                        </td>
                        <td class="cg-desc">{{ group.description || '—' }}</td>
                        <td class="text-center cg-date">{{ formatDate(group.lastMsgDateFormatted) }}</td>
                        <td class="text-center">
                            <span class="cg-permission-badge" :class="{ 'cg-admin-only': group.onlyAllowAdminToSendMsg }">
                                <i :class="group.onlyAllowAdminToSendMsg ? 'fas fa-lock' : 'fas fa-users'"></i>
                                {{ group.onlyAllowAdminToSendMsg ? 'Admin Only' : 'All Members' }}
                            </span>
                        </td>
                        <td class="cg-actions-cell">
                            <div class="cg-actions">
                                <button class="cg-action-btn cg-btn-messages" @click="viewMessages(group.id)" title="View Messages">
                                    <i class="fas fa-comments"></i>
                                </button>
                                <AllowPermission :permission="PERMISSION_CODES.EDIT_COMMUNICATION_GROUPS">
                                    <button class="cg-action-btn cg-btn-edit" @click="enableEditing(group)" title="Edit">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                </AllowPermission>
                                <AllowPermission :permission="PERMISSION_CODES.EDIT_COMMUNICATION_GROUPS">
                                    <button class="cg-action-btn cg-btn-delete" @click="deleteItem(group.id)" title="Delete">
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                </AllowPermission>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { createField } from "../../utils/common"
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            loading: true,
            employees: [],
            managers: [],
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Description*", "description", "text", true),
            ],
            items: [],
            showForm: false,
            editMode: false,
            editData: null,
        }
    },
    components: { Form, RightSideModal, AllowPermission },
    async mounted() {
        await this.fetchUsersForGroupCreation()
        this.createFields = this.createFields.concat([
            createField(
                "Select Managers/Group Admins*",
                "managerIds",
                "multiselect",
                true,
                "",
                this.managers.map((item) => ({
                    id: item.id,
                    title: `${item.firstName || ""} ${item.lastName || ""}`,
                }))
            ),
            createField(
                "Select Employees/Users*",
                "employeeIds",
                "multiselect",
                true,
                "",
                this.employees.map((item) => ({
                    id: item.id,
                    title: `${item.firstName || ""} ${item.lastName || ""}`,
                }))
            ),
            createField("Group Thumbnail", "thumbnailUrl", "image", false),
            createField(
                "Only Allow Admins To Send Messages",
                "onlyAllowAdminToSendMsg",
                "switch",
                false
            ),
        ])
        await this.fetchData()
        this.loading = false
    },
    methods: {
        closeForm() { this.showForm = false; this.editMode = false; this.editData = null },
        handleFormSuccess() { this.closeForm(); this.fetchData() },
        enableEditing(item) { this.editMode = true; this.editData = { ...item }; this.showForm = true; window.scrollTo({ top: 0, behavior: "smooth" }) },
        async viewMessages(id) {
            this.$router.push(`communication-message/${id}`)
        },
        async fetchData() {
            try {
                const res = await http.get("/communication-groups")
                this.items = res.data.result || []
            } catch (err) {
                console.log("error fetching groups:", err)
            }
        },
        async fetchUsersForGroupCreation() {
            try {
                const res = await http.get("/communication-groups/init")
                const resData = res.data.result
                this.employees = resData.employees
                this.managers = resData.managers
            } catch (err) {
                console.log("error in fetching communication groups", err)
            }
        },
        async deleteItem(id) {
            if (!confirm("Delete this group?")) return
            try { await http.delete(`/communication-groups/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch (e) { this.$toast.error("Error deleting") }
        },
        formatDate(date) {
            if (!date) return "—"
            return new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.cg-page { animation: cgFade .3s ease; }
@keyframes cgFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.cg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}

.cg-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cg-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.cg-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.cg-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}

.cg-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover {
        background: $primaryDark;
    }
}

/* Loading / Empty */
.cg-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span {
        font-size: 13px;
        color: $gray400;
    }
}

.cg-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: cgSpin .7s linear infinite;
}

@keyframes cgSpin { to { transform: rotate(360deg); } }

.cg-empty {
    text-align: center;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 {
        font-size: 14px;
        font-weight: 700;
        color: $gray600;
        margin: 0 0 4px;
    }

    p {
        font-size: 12px;
        color: $gray400;
        margin: 0;
    }
}

.cg-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i {
        font-size: 20px;
        color: $gray300;
    }
}

/* Table */
.cg-table-wrap {
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;
    overflow: hidden;
}

.cg-table {
    width: 100%;
    border-collapse: collapse;
}

.cg-table thead {
    background: $gray50;
    border-bottom: 2px solid $gray100;
}

.cg-table th {
    padding: 12px 14px;
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    text-align: left;
}

.cg-table td {
    padding: 12px 14px;
    font-size: 13px;
    color: $gray700;
    font-weight: 500;
    border-bottom: 1px solid $gray100;
    vertical-align: middle;
}

.cg-table tbody tr {
    transition: background $transitionFast;

    &:last-child td {
        border-bottom: none;
    }

    &:hover {
        background: $gray50;
    }
}

.text-center {
    text-align: center;
}

/* Name Cell */
.cg-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cg-group-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.cg-avatar-default {
    background: linear-gradient(135deg, #eff6ff, #e0f2fe);
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.cg-name {
    font-weight: 600;
    color: $gray800;
}

.cg-desc {
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $gray600;
}

.cg-date {
    font-size: 12px;
    color: $gray400;
}

/* Permission Badge */
.cg-permission-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 600;
    background: #f0fdf4;
    color: #16a34a;

    i {
        font-size: 10px;
    }

    &.cg-admin-only {
        background: #fef3c7;
        color: #d97706;
    }
}

/* Actions Cell */
.cg-actions-cell {
    text-align: center;
    padding: 12px 14px !important;
}

.cg-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}

.cg-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    font-size: 11px;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    flex-shrink: 0;

    i {
        font-size: 11px;
    }
}

.cg-btn-messages {
    color: #2563eb;
    border-color: rgba(37, 99, 235, 0.2);

    &:hover {
        background: #eff6ff;
        border-color: rgba(37, 99, 235, 0.3);
    }
}

.cg-btn-edit {
    color: $primaryColor;
    border-color: rgba(0, 94, 137, 0.2);

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.3);
    }
}

.cg-btn-delete {
    color: $danger;
    border-color: rgba(220, 38, 38, 0.2);

    &:hover {
        background: #fef2f2;
        border-color: rgba(220, 38, 38, 0.3);
    }
}

/* Responsive */
@media (max-width: 1200px) {
    .cg-table-wrap {
        overflow-x: auto;
    }
}

@media (max-width: 768px) {
    .cg-header {
        flex-direction: column;
        align-items: stretch;
    }

    .cg-desc {
        display: none;
    }
}
</style>
